import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Dropdown, Empty, List, Space } from 'antd'
import { BellOutlined } from '@ant-design/icons'
import { formatDistanceToNow } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { allNotifications } from 'redux/notifications/action'
import {
  getUserNotifications,
  readNotification,
  readUserNotification,
} from 'services/axios/notification'
import { getLoggedInUserInfo } from 'utils'
import { subscribeToNotifications } from 'socket'
import styles from './style.module.scss'

const NotificationBell = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [showNotificationList, setShowNotificationList] = useState(false)
  const userId = getLoggedInUserInfo()?.userId

  const notifications = useSelector((state) => state?.notifications?.notifications) || []

  const loadNotifications = useCallback(async () => {
    if (!userId) return

    try {
      const response = await getUserNotifications({ limit: 20, unread: true })

      const nextNotifications = response?.data?.data?.notifications || []

      dispatch(allNotifications(nextNotifications))
    } catch (error) {
      console.error('Notification fetch failed:', error)
    }
  }, [dispatch, userId])

  useEffect(() => {
    if (!userId) return undefined
    loadNotifications()
    return subscribeToNotifications({
      onNotification: loadNotifications,
      onReconnect: loadNotifications,
    })
  }, [userId, loadNotifications])

  // useEffect(() => {
  //   if (!userId) return

  //   loadNotifications()
  // }, [userId])

  const handleDropdownVisibleChange = async (visible) => {
    setShowNotificationList(visible)

    if (visible) {
      await loadNotifications()
    }
  }

  const removeNotificationList = async () => {
    if (!userId) return

    try {
      await readUserNotification()

      dispatch(allNotifications([]))

      setShowNotificationList(false)
    } catch (error) {
      console.error('Failed to mark notifications as read:', error)
    }
  }

  const handleNotificationClick = async (item) => {
    try {
      if (item?.id) {
        await readNotification(item.id)
        dispatch(
          allNotifications(
            notifications.filter((notificationItem) => notificationItem?.id !== item.id),
          ),
        )
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }

    // Leave approval notifications are informational only. Once read, remove
    // them from the dropdown without sending the user to a non-existent page.
    if (item?.type === 'leave.approved') {
      setShowNotificationList(false)
      return
    }

    const actionUrlAliases = {
      '/events': '/dashboard',
      '/attendance/attendance-regularisation': '/attendance-regularisation',
    }
    const actionUrl = actionUrlAliases[item?.actionUrl] || item?.actionUrl

    if (actionUrl?.startsWith('/')) {
      history.push(actionUrl)
      setShowNotificationList(false)
    }
  }

  const dropdownContent = (
    <div className={styles.dropdown}>
      <div className={styles.header}>
        <strong>Notifications</strong>

        {notifications.length > 0 && (
          <Button size="small" type="link" onClick={removeNotificationList}>
            Mark all as read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No new notifications" />
      ) : (
        <List
          className={styles.list}
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item
              onClick={() => handleNotificationClick(item)}
              style={{ cursor: item?.actionUrl ? 'pointer' : 'default' }}
            >
              <div className={styles.item}>
                <div className={styles.message}>{item?.notification}</div>

                <div className={styles.time}>
                  {item?.createdAt
                    ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })
                    : ''}
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  )

  return (
    <Dropdown
      overlay={dropdownContent}
      visible={showNotificationList}
      placement="bottomRight"
      trigger={['click']}
      onVisibleChange={handleDropdownVisibleChange}
    >
      <Space className={styles.trigger}>
        <Badge count={notifications.length} size="small">
          <BellOutlined className={styles.icon} />
        </Badge>
      </Space>
    </Dropdown>
  )
}

export default NotificationBell
